import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { instance } from "../../axios/axios";
import axios from "axios";
import { startImpersonation, stopImpersonation } from "../auth/authSlice";

const initialState = {
  trainingTaskPage: {
    data: [],
    loading: false,
    error: null,
    lastFetched: null,
    firstFetch: false,
  },
  truckDetail: {
    data: [],
    loading: false,
    error: null,
  },
  allTasks: {
    data: [],
    loading: false,
    backgroundRefetching: false,
    error: null,
    lastFetched: null,
  },
  taskManagerTasks: {
    data: [],
    loading: false,
    error: null,
    lastFetched: null,
  },
  repTrainingTasks: {
    data: [],
    loading: false,
    error: null,
    lastFetched: null,
  },
  manuelPDFs: [],
  inventorySubmission: {
    isSubmitting: false,
    submissionType: null,
    error: null,
    result: null, // { success: true, message: '...' } or null
  },
};
// Thunk for fetching training task page details
export const fetchTrainingTaskPageDetails = createAsyncThunk(
  "data/fetchTrainingTaskPageDetails",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth?.token;

      const response = await instance.get(
        `/training/portal/get-training-task-page`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "An error occurred."
      );
    }
  }
);

// Thunk for fetching all tasks
export const fetchAllTasks = createAsyncThunk(
  "data/fetchAllTasks",
  async (options = {}, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth?.token;

      const response = await instance.get(`/task/portal/fetch-all-tasks`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "An error occurred."
      );
    }
  }
);

// Thunk for submitting inventory inspection (runs in background)
export const submitInventoryInspection = createAsyncThunk(
  "data/submitInventoryInspection",
  async ({ formDataToSend, type }, { rejectWithValue, dispatch, getState }) => {
    try {
      // Send the caller's identity with the submit.
      //
      // WHY (thread #1241, 2026-08-27): every other thunk in this file attaches
      // the bearer token, this one never did, and there is no axios request
      // interceptor (src/axios/axios.js creates a bare instance) — so
      // /inventory/portal/submit-inventory-inspection-bulk has been reachable
      // with no identity at all. The BFF route carries no `checkUser` either,
      // which is the only reason the feature works today.
      //
      // This is deliberately the FIRST of two steps. Adding `checkUser` on the
      // BFF while the portal sends nothing would take inventory submits down
      // company-wide, so the client starts sending the token now; the BFF gate
      // goes on only once this is live in production. Sending a header the
      // server does not yet read is a no-op, so this half is safe to merge
      // alone and safe to merge first.
      const token = getState()?.auth?.token;
      const response = await instance.post(
        '/inventory/portal/submit-inventory-inspection-bulk',
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (response.data.status === "Success" || response.data.status === "Partial") {
        // Trigger background refetch whether fully or partially successful, so
        // items that did complete clear off the rep's list immediately.
        dispatch(fetchAllTasks({ backgroundRefetch: true }));
        return {
          success: response.data.status === "Success",
          partial: response.data.status === "Partial",
          data: response.data,
          type
        };
      } else {
        return rejectWithValue(response.data.error || response.data.message || 'Operation failed');
      }
    } catch (error) {
      // Re-fetch tasks so any items that failed to submit reappear on the list.
      // Without this, a background 400 silently removes the task from view.
      dispatch(fetchAllTasks({ backgroundRefetch: true }));
      return rejectWithValue(
        error.response?.data?.error || error.response?.data?.message || error.message || "An error occurred."
      );
    }
  }
);

// Autosave one or more inventory-inspection lines the rep has already filled
// in (Phase 6, thread #1526 r15).
//
// WHY THIS IS A SEPARATE THUNK AND NOT A SECOND CALL TO THE ONE ABOVE.
// submitInventoryInspection does two things the phone screen cannot live with
// on a debounce: it flips `inventorySubmission.isSubmitting`, which makes the
// whole form render a full-screen spinner, and on success it dispatches
// fetchAllTasks({backgroundRefetch:true}), which does the same. Autosaving
// while a rep types would therefore blank the screen they are working on,
// several times a minute. Same endpoint, same payload shape, same token —
// different bookkeeping. Nothing here dispatches, so no reducer needs to know.
//
// This thunk deliberately keeps NO redux state. Its result is returned to the
// caller, which is the only place that has the formData it describes.
export const autosaveInventoryInspection = createAsyncThunk(
  "data/autosaveInventoryInspection",
  async ({ formDataToSend }, { rejectWithValue, getState }) => {
    try {
      const token = getState()?.auth?.token;
      const response = await instance.post(
        '/inventory/portal/submit-inventory-inspection-bulk',
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      return { status: response.data.status, data: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || error.response?.data?.message || error.message || "Autosave failed."
      );
    }
  }
);

// Thunk for fetching task manager tasks
export const fetchTaskManagerTasks = createAsyncThunk(
  "data/fetchTaskManagerTasks",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth?.token;

      const response = await instance.get(`/task/portal/fetch-task-manager-tasks`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "An error occurred."
      );
    }
  }
);

export const fetchTruckDetailById = createAsyncThunk(
  'data/fetchTruckDetailById', async ({ id }, { rejectWithValue, getState }) => {
    try {
      const token = getState().auth?.token;
      const result = await instance.get(`/truck/portal/get/truck-detail-by-recId/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      return result?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error?.message || "An error occurred.")
    }
  }
)

export const updateTrainingTaskCompletion = createAsyncThunk(
  "data/updateTrainingTaskCompletion",
  async ({ recId, uploadedFiles, completedTaskManager }, { getState, dispatch }) => {
    try {
      const state = getState();
      const trainingData = state.data.trainingTaskPage.data;
      const updatedData = JSON.parse(JSON.stringify(trainingData));

      updatedData.data.map((item) => {
        if (item["Record Id"] === recId) {
          // Only set completion flags when this is a real submit (completedTaskManager is
          // "true"/"false"), not a draft save (completedTaskManager is undefined).
          // Fixes the case where the post-submit silentRefetch is aborted (e.g. the user
          // taps another task, triggering a visit-count refetch that cancels the in-flight
          // one) — without this the task stays blue despite Airtable being updated.
          if (completedTaskManager !== undefined) {
            item["Task Completed (Employee Checkbox)"] = true;
            item["Completed Task (Task Manager)"] = completedTaskManager === "true";
            item["Incomplete Task"] = false;
          }

          // Route uploaded files to the correct local field to match BFF Airtable writes:
          // .mov files → "mov file url's" (BFF calls convert-mov-to-mp4 and stores original there)
          // everything else → "Google Drive Link"
          const movUrls = uploadedFiles
            .filter((f) => (f.filename || "").toLowerCase().endsWith(".mov"))
            .map((f) => f.url);
          const nonMovUrls = uploadedFiles
            .filter((f) => !(f.filename || "").toLowerCase().endsWith(".mov"))
            .map((f) => f.url);
          if (movUrls.length > 0) {
            const existingMov = item["mov file url's"]
              ? item["mov file url's"].split(",").map((u) => u.trim())
              : [];
            const allMov = [...existingMov, ...movUrls.filter((u) => !existingMov.includes(u))];
            item["mov file url's"] = allMov.join(",");
          }
          if (nonMovUrls.length > 0) {
            const existingLinks = item["Google Drive Link"]
              ? item["Google Drive Link"].split(", ")
              : [];
            const allLinks = [...existingLinks, ...nonMovUrls.filter((u) => !existingLinks.includes(u))];
            item["Google Drive Link"] = allLinks.join(", ");
          }
        }
        return item;
      });

      dispatch(dataSlice.actions.updateTrainingTaskData(updatedData));
      return updatedData;
    } catch (error) {
      throw new Error("Failed to update task completion status");
    }
  }
);

// Silent refetch all tasks thunk
export const silentRefetchAllTasks = createAsyncThunk(
  "data/silentRefetchAllTasks",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth?.token;

      const response = await instance.get(`/task/portal/fetch-all-tasks`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "An error occurred."
      );
    }
  }
);

let currentAbortController = null;
// Silent refetch training task page thunk
export const silentRefetchTrainingTaskPage = createAsyncThunk(
  "data/silentRefetchTrainingTaskPage",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth?.token;
      console.log(currentAbortController, 'currentAbortController');
      if (currentAbortController) {
        currentAbortController.abort();
      }

      currentAbortController = new AbortController();
      const response = await instance.get(
        `/training/portal/get-training-task-page`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: currentAbortController.signal,
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'CanceledError' || error.name === 'AbortError') {
        console.log('Previous request aborted');
        return rejectWithValue('request Rejected')
      }
      return rejectWithValue(
        error.response?.data?.message || error.message || "An error occurred."
      );
    }
  }
);

//stroe data in deduction table
// Silent refetch training task page thunk
export const creatDeductionData = createAsyncThunk(
  "data/creatDeductionData",
  async (payload, { rejectWithValue, getState }) => {
    try {
      const token = getState().auth?.token;
      const response = await instance.post(`/truck/portal/add-deduction-data`, payload,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "An error occurred."
      );
    }
  }
);

export const updateTrainingWatchedSecs = createAsyncThunk(
  "data/updateTrainingWatchedSecs",
  async ({ recId, sessionSecs }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth?.token;
      const existingSecs = state.contentTraining?.data?.watched_training_task_secs || 0;
      const watchedSecs = existingSecs + sessionSecs;
      const response = await instance.post(
        `/training/portal/update-training-task`,
        { recId, watchedSecs },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "An error occurred."
      );
    }
  }
);

export const updateTrainingViewCount = createAsyncThunk(
  "data/updateTrainingViewCount",
  async ({ recId, viewCount }, { rejectWithValue, getState }) => {
    try {
      const token = getState().auth?.token;
      const response = await instance.post(
        `/training/portal/update-training-task`, { recId, viewCount }, { headers: token ? { Authorization: `Bearer ${token}` } : {}, }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "An error occurred."
      );
    }
  }
);

// Visibility-aware page-dwell heartbeat for the training task page.
// Caller passes the cumulative `taskOpenSecs` (existing-from-Airtable + accumulated-this-session) so the
// thunk doesn't depend on Redux state staying in sync — the caller carries the running total in a ref.
export const updateTaskOpenSecs = createAsyncThunk(
  "data/updateTaskOpenSecs",
  async ({ recId, taskOpenSecs }, { rejectWithValue, getState }) => {
    try {
      const token = getState().auth?.token;
      const response = await instance.post(
        `/training/portal/update-training-task`,
        { recId, taskOpenSecs },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "An error occurred."
      );
    }
  }
);

// Thunk for fetching rep training tasks
export const fetchRepTrainingTasks = createAsyncThunk(
  "data/fetchRepTrainingTasks",
  async ({ employeeRecordId }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth?.token;

      const response = await instance.post(
        `/training/portal/rep-tasks`,
        { employeeRecordId },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message || "An error occurred."
      );
    }
  }
);

const dataSlice = createSlice({
  name: "data",
  initialState,
  reducers: {
    refetchTrainingTaskPage: (state) => {
      state.trainingTaskPage.lastFetched = null;
    },
    refetchAllTasks: (state) => {
      state.allTasks.lastFetched = null;
    },
    setManuelPDFs: (state, { payload }) => {
      state.manuelPDFs = payload;
    },
    clearAllData: () => initialState,
    updateTrainingTaskData: (state, { payload }) => {
      state.trainingTaskPage.data = payload;
    },
    clearRepTrainingTasks: (state) => {
      state.repTrainingTasks = {
        data: [],
        loading: false,
        error: null,
        lastFetched: null,
      };
    },
    clearInventorySubmissionResult: (state) => {
      state.inventorySubmission.result = null;
      state.inventorySubmission.error = null;
    },
    updateTaskFieldById: (state, { payload }) => {
      const { taskId, field, value } = payload;
      const updateInList = (list) => {
        if (!Array.isArray(list)) return;
        const task = list.find((t) => t.id === taskId);
        if (task?.fields) task.fields[field] = value;
      };
      updateInList(state.allTasks.data?.data?.curWeekData);
      updateInList(state.allTasks.data?.data?.prevWeekData);
    },
  },
  extraReducers: (builder) => {
    builder
      // Identity switch (Quick Log impersonation start AND swap-back) must
      // wipe every per-user cache in this slice. These caches (training,
      // tasks, etc.) are keyed implicitly to the current effective user and
      // are TTL-gated (e.g. the Training page only refetches if its cached
      // copy is >45 min old). Without this reset, quick-logging into another
      // employee shows the impersonator's own stale cached data — e.g. the
      // viewer's empty training task list under the target's portal.
      .addCase(startImpersonation, () => initialState)
      .addCase(stopImpersonation, () => initialState)
      // Fetch Training Task Page Details
      .addCase(fetchTrainingTaskPageDetails.pending, (state) => {
        state.trainingTaskPage.loading = true;
        state.trainingTaskPage.error = null;
        // Glitch #612 (2026-10-15): lastFetched is stamped on FULFILLED only —
        // never here. Stamping it on `pending` made a FAILED fetch count as a
        // fresh one: the consumers (TrainingContent.jsx:52, Academy.jsx:42,
        // Home.jsx's useTimedRefetch) refetch only when `lastFetched` is null
        // or older than 45 min, so one transient failure both set `error` AND
        // suppressed the retry — leaving "Error loading data. Please try
        // again." pinned on the Training tab until a full page reload wiped
        // the in-memory store. fetchAllTasks and fetchTaskManagerTasks already
        // stamp on fulfilled only; this thunk was the lone exception.
        state.trainingTaskPage.firstFetch = true;
      })
      .addCase(fetchTrainingTaskPageDetails.fulfilled, (state, { payload }) => {
        state.trainingTaskPage.data = payload;
        state.trainingTaskPage.loading = false;
        state.trainingTaskPage.error = null;
        state.trainingTaskPage.lastFetched = Date.now();
      })
      .addCase(fetchTrainingTaskPageDetails.rejected, (state, { payload }) => {
        state.trainingTaskPage.loading = false;
        state.trainingTaskPage.error =
          payload || "An error occurred while fetching training data.";
      })
      // Fetch All Tasks
      .addCase(fetchAllTasks.pending, (state, action) => {
        // Skip loading state for background refetch, but track it separately
        if (action.meta.arg?.backgroundRefetch) {
          state.allTasks.backgroundRefetching = true;
        } else {
          state.allTasks.loading = true;
        }
        state.allTasks.error = null;
      })
      .addCase(fetchAllTasks.fulfilled, (state, { payload }) => {
        state.allTasks.data = payload;
        state.allTasks.loading = false;
        state.allTasks.backgroundRefetching = false;
        state.allTasks.error = null;
        state.allTasks.lastFetched = Date.now();
      })
      .addCase(fetchAllTasks.rejected, (state, { payload }) => {
        state.allTasks.loading = false;
        state.allTasks.backgroundRefetching = false;
        state.allTasks.error =
          payload || "An error occurred while fetching tasks.";
      })
      // Fetch Task Manager Tasks
      .addCase(fetchTaskManagerTasks.pending, (state) => {
        state.taskManagerTasks.loading = true;
        state.taskManagerTasks.error = null;
      })
      .addCase(fetchTaskManagerTasks.fulfilled, (state, { payload }) => {
        state.taskManagerTasks.data = payload;
        state.taskManagerTasks.loading = false;
        state.taskManagerTasks.error = null;
        state.taskManagerTasks.lastFetched = Date.now();
      })
      .addCase(fetchTaskManagerTasks.rejected, (state, { payload }) => {
        state.taskManagerTasks.loading = false;
        state.taskManagerTasks.error =
          payload || "An error occurred while fetching task manager tasks.";
      })
      // Silent Refetch All Tasks
      .addCase(silentRefetchAllTasks.pending, (state) => {
        state.allTasks.loading = true;
        state.allTasks.error = null;
      })
      .addCase(silentRefetchAllTasks.fulfilled, (state, { payload }) => {
        state.allTasks.data = payload;
        state.allTasks.lastFetched = Date.now();
        state.allTasks.loading = false;
      })
      .addCase(silentRefetchAllTasks.rejected, (state, { payload }) => {
        state.allTasks.error =
          payload || "An error occurred while fetching tasks.";
        state.allTasks.loading = false;
      })
      // truck detail by id process
      .addCase(fetchTruckDetailById.pending, (state) => {
        state.truckDetail = { ...state.truckDetail, loading: true, error: null }
      })
      .addCase(fetchTruckDetailById.fulfilled, (state, { payload }) => {
        state.truckDetail = { ...state.truckDetail, loading: false, data: payload }
      })
      .addCase(fetchTruckDetailById.rejected, (state, { payload }) => {
        state.truckDetail = { ...state.truckDetail, loading: false, error: payload }
        state.allTasks.loading = false;
      })
      // Silent Refetch Training Task Page
      .addCase(
        silentRefetchTrainingTaskPage.fulfilled,
        (state, { payload }) => {
          state.trainingTaskPage.data = payload;
          state.trainingTaskPage.lastFetched = Date.now();
        }
      )
      .addCase(silentRefetchTrainingTaskPage.rejected, (state, { payload }) => {
        console.log(payload, 'payload:::::::::');

        state.trainingTaskPage.error =
          payload === 'request Rejected' ? '' : payload || "An error occurred while fetching training data.";
      })
      // Fetch Rep Training Tasks
      .addCase(fetchRepTrainingTasks.pending, (state) => {
        state.repTrainingTasks.loading = true;
        state.repTrainingTasks.error = null;
      })
      .addCase(fetchRepTrainingTasks.fulfilled, (state, { payload }) => {
        state.repTrainingTasks.data = payload;
        state.repTrainingTasks.loading = false;
        state.repTrainingTasks.error = null;
        state.repTrainingTasks.lastFetched = Date.now();
      })
      .addCase(fetchRepTrainingTasks.rejected, (state, { payload }) => {
        state.repTrainingTasks.loading = false;
        state.repTrainingTasks.error =
          payload || "An error occurred while fetching rep training tasks.";
      })
      // Submit Inventory Inspection (background)
      .addCase(submitInventoryInspection.pending, (state, action) => {
        state.inventorySubmission.isSubmitting = true;
        state.inventorySubmission.submissionType = action.meta.arg?.type || null;
        state.inventorySubmission.error = null;
        state.inventorySubmission.result = null;
      })
      .addCase(submitInventoryInspection.fulfilled, (state, { payload }) => {
        state.inventorySubmission.isSubmitting = false;
        state.inventorySubmission.submissionType = null;
        state.inventorySubmission.result = {
          success: payload.success,
          partial: payload.partial || false,
          type: payload.type,
          message: payload.data?.message || (payload.success
            ? (payload.type === 'submit' ? 'All tasks submitted successfully' : 'All tasks saved successfully')
            : 'Some items could not be submitted — see items remaining on your list'),
          //! CORRECTED 2026-08-27 (thread #1233): prefer the BFF's new `failed`
          //! list. `unchanged` mixes real failures with benign "item was not
          //! modified" skips on a save, so listing it as-is told the rep an
          //! untouched item had a problem.
          failedItems: payload.data?.data?.failed || payload.data?.data?.unchanged || [],
        };
      })
      .addCase(submitInventoryInspection.rejected, (state, { payload }) => {
        state.inventorySubmission.isSubmitting = false;
        state.inventorySubmission.submissionType = null;
        state.inventorySubmission.error = payload || "Submission failed — please try again.";
        const failedItemName = typeof payload === 'object' && payload?.details?.itemName
          ? payload.details.itemName
          : null;
        const failureReason = typeof payload === 'object'
          ? (payload?.details?.error || payload?.message || null)
          : (typeof payload === 'string' ? payload : null);
        state.inventorySubmission.result = {
          success: false,
          //! CORRECTED 2026-08-26 (thread #1231): when the BFF returns a failure
          //! WITHOUT an item name (e.g. a SERVER_ERROR wrapping an unexpected
          //! exception), show the server's own message instead of the generic
          //! "Please find the item and try again." — that generic toast hid the
          //! real error and read as a data problem to the rep. The known
          //! item-level failures already carry details.itemName and use the
          //! first branch.
          message: failedItemName
            ? `Could not save "${failedItemName}"${failureReason ? ` — ${failureReason}` : ''}. Your task list reloaded. Please check that item and try again.`
            : (failureReason
                ? `Submission failed — ${failureReason}`
                : "Submission failed — your task list reloaded. Please find the item and try again."),
        };
      });
  },
});



export const {
  refetchTrainingTaskPage,
  refetchAllTasks,
  setManuelPDFs,
  clearAllData,
  updateTrainingTaskData,
  clearRepTrainingTasks,
  clearInventorySubmissionResult,
  updateTaskFieldById,
} = dataSlice.actions;

export default dataSlice.reducer;
